import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const COOKIE_KEY = "kupto_cookie_consent_v1";

const readConsent = () => {
  try {
    return localStorage.getItem(COOKIE_KEY);
  } catch {
    return null;
  }
};

const writeConsent = (val) => {
  try {
    localStorage.setItem(COOKIE_KEY, val);
  } catch {}
};

const CookieConsent = ({
  policyPath = "/privacy-policy",
  cookieNoticePath = "/cookie-notice",
  position = "bottom-left",
}) => {
  const location = useLocation();

  const [consent, setConsent] = useState(() => readConsent());

  // ✅ detect app/mobile
  const [isApp, setIsApp] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsApp(Boolean(e.matches));

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    setIsApp(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // ✅ keep consent updated after route changes
  useEffect(() => {
    setConsent(readConsent());
  }, [location.pathname]);

  const shouldShow = useMemo(() => {
    // ✅ hide on app/mobile
    if (isApp) return false;

    // ✅ show only when not decided
    return consent !== "accepted" && consent !== "rejected";
  }, [consent, isApp]);

  const accept = () => {
    writeConsent("accepted");
    setConsent("accepted");
    window.dispatchEvent(new CustomEvent("kuptoCookieConsent", { detail: "accepted" }));
  };

  const reject = () => {
    writeConsent("rejected");
    setConsent("rejected");
    window.dispatchEvent(new CustomEvent("kuptoCookieConsent", { detail: "rejected" }));
  };

  // ✅ return AFTER hooks
  if (!shouldShow) return null;

  return (
    <div className={`ku-cookie ku-cookie--${position}`} role="dialog" aria-live="polite">
      <div className="ku-cookie__title">This website uses cookies</div>

      <div className="ku-cookie__text">
        This website uses cookies. For further information on how we use cookies you can read our{" "}
        <Link to={policyPath} className="ku-cookie__link">
          Privacy Policy
        </Link>
        .
      </div>

      <div className="ku-cookie__actions">
        <button type="button" className="ku-cookie__btn ku-cookie__btn--ghost" onClick={reject}>
          Decline
        </button>

        <button type="button" className="ku-cookie__btn" onClick={accept}>
          Accept cookies
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
